// Google OAuth implicit flow (response_type=id_token). Token is persisted
// to sessionStorage so it survives page refreshes. Session lasts 24 hours.

const STATE_KEY = "ai_proto_oauth_state";
const TOKEN_KEY = "ai_proto_token";
const SESSION_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

interface StoredToken { idToken: string; email: string; expiresAt: number; }
let currentToken: StoredToken | null = null;

interface Config { googleClientId: string; apiUrl: string; }
let cfg: Config | null = null;

export async function initAuth(config: Config): Promise<void> {
  cfg = config;

  // Handle OAuth callback
  const hash = new URLSearchParams(window.location.hash.slice(1));
  if (hash.has("id_token")) {
    const savedState = sessionStorage.getItem(STATE_KEY);
    if (hash.get("state") !== savedState) throw new Error("OAuth state mismatch");
    const idToken = hash.get("id_token")!;
    const { email, exp } = parseIdToken(idToken);
    const tokenExpiry = exp * 1000;
    const sessionExpiry = Date.now() + SESSION_TTL_MS;
    currentToken = { idToken, email, expiresAt: Math.min(tokenExpiry, sessionExpiry) };
    sessionStorage.setItem(TOKEN_KEY, JSON.stringify(currentToken));
    history.replaceState(null, "", window.location.pathname);
    return;
  }

  // Restore from sessionStorage
  const stored = sessionStorage.getItem(TOKEN_KEY);
  if (stored) {
    try {
      const parsed = JSON.parse(stored) as StoredToken;
      if (parsed.expiresAt > Date.now()) {
        currentToken = parsed;
      } else {
        sessionStorage.removeItem(TOKEN_KEY);
      }
    } catch {
      sessionStorage.removeItem(TOKEN_KEY);
    }
  }
}

export function isSignedIn(): boolean {
  return !!currentToken && currentToken.expiresAt > Date.now();
}

export function currentEmail(): string | null {
  return currentToken?.email ?? null;
}

export function idToken(): string {
  if (!currentToken) throw new Error("not signed in");
  return currentToken.idToken;
}

export async function signIn(): Promise<void> {
  if (!cfg) throw new Error("auth not initialized");
  const state = randomString(24);
  sessionStorage.setItem(STATE_KEY, state);

  const nonce = randomString(24);
  const params = new URLSearchParams({
    client_id: cfg.googleClientId,
    response_type: "id_token",
    scope: "openid email profile",
    redirect_uri: window.location.origin + window.location.pathname,
    state,
    nonce,
    prompt: "select_account",
    hd: "rewind.io",
  });
  window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

function parseIdToken(token: string): { email: string; exp: number } {
  const parts = token.split(".");
  if (parts.length < 2) throw new Error("bad id_token");
  let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
  while (b64.length % 4) b64 += "=";
  const payload = JSON.parse(atob(b64));
  return { email: payload.email as string, exp: payload.exp as number };
}

function randomString(len: number): string {
  const bytes = new Uint8Array(len);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "").slice(0, len);
}
